import React, {createContext, use, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useLazyQuery, useMutation} from "@apollo/client";
import jwtDecode from "jwt-decode";
import {
  GET_CURRENT_USER,
  UPDATE_LAST_LOGIN,
  SYNC_USER_ORGANIZATION_MFA,
} from "../../../graphql/userQueries";
import {initializeMetricsForUser} from "../../../resources/metrics";
import {getDisplayName} from "../../../resources/users";
import {useAuth} from "../../authentication/Context/AuthProvider";
import MFASetupForm from "../../authentication/MFA/MFASetupForm";
import Loader from "../../Loader";
import {findWhere, includes, isArray} from "underscore";

const UserContext = createContext(undefined);

const UserProvider = ({children}) => {
  const {authSession, logout, orgName, setErrorMessage} = useAuth();

  const [getCurrentUser, {data, stopPolling}] = useLazyQuery(GET_CURRENT_USER, {
    pollInterval: 5000,
  });
  const [updateLastLogin] = useMutation(UPDATE_LAST_LOGIN);
  const [syncUserMfa, {data: mfaData, error: mfaError}] = useMutation(SYNC_USER_ORGANIZATION_MFA);
  const [user, setUser] = useState();
  const [phoneRequired, setPhoneRequired] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    const initializeUser = async () => {
      await syncUserMfa();
      await getCurrentUser();
    };

    initializeUser();
  }, []);

  useEffect(() => {
    const payload = jwtDecode(authSession.IdToken);
    const provider = payload["cognito:username"]?.split("_")[0];
    if (
      ["googlesaml", "okta", "onelogin"].includes(provider) &&
      isArray(payload?.identities) &&
      (findWhere(payload.identities, {providerName: "GoogleSAML"}) ||
        findWhere(payload.identities, {providerName: "Okta"}) ||
        findWhere(payload.identities, {providerName: "OneLogin"}))
    ) {
      return;
    }
    if (mfaError) {
      setErrorMessage("Unable to verify MFA settings. Please try logging in again.");
      logout();
      return;
    }
    if (mfaData) {
      if (mfaData.syncMfaRequired === "phone_required") {
        setPhoneRequired(true);
      }
      if (mfaData.syncMfaRequired === "sms_mfa_enabled") {
        setErrorMessage(
          "SMS MFA is now enabled.  Please try logging in again, " +
          "and when prompted, enter the code sent to the phone number given in your profile.",
        );
        logout();
      }
      if (mfaData.syncMfaRequired === "invite_needed") {
        setErrorMessage("Please request an invite from this organization.");
        logout();
      }
      if (mfaData.syncMfaRequired === "user_configured") {
        setErrorMessage("One time configuration complete. Please log in again.");
        logout();
      }
    }
  }, [mfaData, mfaError]);

  useEffect(() => {
    if (!data || !data.currentUser || !data.authorization) {
      return;
    }
    stopPolling();

    if (!user && !phoneRequired) {
      updateLastLogin({variables: {user: {id: data.currentUser.id}}});
    }

    const accessPayload = jwtDecode(authSession.AccessToken);
    const isKeycloak = accessPayload.iss?.includes("/realms/");
    const isCognito =
      "Cognito" in (data?.currentUser?.providers ?? {}) &&
      (data.currentUser?.providers.Cognito.uuid === accessPayload.sub ||
        includes(data.currentUser?.providers.Cognito?.uuids, accessPayload.sub));

    setUser({
      ...data.currentUser,
      profilePicUrl: data.currentUser?.profileDocumentPreview ?? null,
      displayName: getDisplayName(data.currentUser),
      isCognito,
      isKeycloak,
      authorization: data.authorization,
    });
    initializeMetricsForUser(orgName, data.currentUser?.email);
  }, [data]);

  if (!mfaData && !mfaError) {
    return <Loader fullViewingHeight />;
  }

  return (
    <UserContext value={{user, setUser}}>{phoneRequired ? <MFASetupForm /> : children}</UserContext>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useUser = () => {
  return use(UserContext);
};

export {UserProvider, useUser, UserContext};
